import React, { useState, useEffect, useRef } from "react";
import { Search, Check } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * LinkPickerField (SOUND-497)
 * -------------------------------------------------------
 * Debounced entity search for one `entityType`, scoped to a client company
 * (mirrors CompanyPickerField's structure). Emits `{ entityId, label }`.
 *
 * Company-scoped types (mid / application / dba / disputifierEnrollment / company)
 * search under `companyId`; processor / partner are global. The backend
 * re-validates the scope invariant on link create, so this is a convenience
 * filter, not the security boundary.
 */

// Per-type search: build the request + map each result to { id, label, sub }.
async function searchEntities(entityType, q, companyId, authUser, headerCompanyId) {
  const run = (route) => makeApiRequest("get", route, authUser, headerCompanyId);

  switch (entityType) {
    case "mid":
    case "disputifierEnrollment": {
      const p = new URLSearchParams();
      if (companyId) p.set("companyId", companyId);
      if (q) p.set("search", q);
      p.set("limit", "10");
      const res = await run(`${apiRoutes.listMids}?${p.toString()}`);
      return (res.data?.data || []).map((m) => ({
        id: m.midId,
        label: m.descriptor || m.name || m.mid || m.midId,
        sub: m.mid || m.processorName,
      }));
    }
    case "application": {
      const p = new URLSearchParams();
      if (companyId) p.set("companyId", companyId);
      if (q) p.set("search", q);
      p.set("limit", "10");
      const res = await run(`${apiRoutes.listMidApplications}?${p.toString()}`);
      return (res.data?.data || []).map((a) => ({
        id: a.applicationId,
        label: a.dbaName || a.clientName || a.processorName || "Application",
        sub: a.processorName || a.clientName,
      }));
    }
    case "dba": {
      // There is no DBA search endpoint. DBAs are company-owned (SOUND-649),
      // and the registration read assembles the company's `dbas` collection
      // onto `dbas[]` — so list those and filter locally. Without this case
      // "dba" fell through to the COMPANY search and offered companies as DBAs.
      if (!companyId) return [];
      const res = await run(apiRoutes.getApplication(companyId));
      const ql = (q || "").toLowerCase();
      return (res.data?.dbas || [])
        .filter((d) => d.dbaId && d.dbaName)
        .filter((d) => !ql || d.dbaName.toLowerCase().includes(ql))
        .slice(0, 10)
        .map((d) => ({ id: d.dbaId, label: d.dbaName, sub: d.websiteUrl || null }));
    }
    case "processor": {
      const res = await run(apiRoutes.getProcessors);
      const ql = (q || "").toLowerCase();
      return (res.data?.data || [])
        .filter((p2) => !ql || (p2.name || "").toLowerCase().includes(ql))
        .slice(0, 10)
        .map((p2) => ({ id: p2.processorId, label: p2.name, sub: p2.processorId }));
    }
    case "partner": {
      const p = new URLSearchParams();
      p.set("companyType", "partner");
      if (q) p.set("search", q);
      p.set("limit", "10");
      const res = await run(`${apiRoutes.getCompanies}?${p.toString()}`);
      return (res.data?.data || []).map((c) => ({
        id: c.companyId,
        label: c.name,
        sub: c.slug,
      }));
    }
    case "company":
    default: {
      const p = new URLSearchParams();
      if (q) p.set("search", q);
      p.set("limit", "10");
      const res = await run(`${apiRoutes.getCompanies}?${p.toString()}`);
      return (res.data?.data || []).map((c) => ({
        id: c.companyId,
        label: c.name,
        sub: c.slug,
      }));
    }
  }
}

export default function LinkPickerField({
  entityType,
  companyId,
  onSelect,
  placeholder = "Search…",
  autoFocus = false,
}) {
  const auth = useAuth();
  const headerCompanyId = useEffectiveCompanyId();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  // Close the floating results on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!auth?.user) return;
      setLoading(true);
      try {
        const items = await searchEntities(
          entityType,
          query,
          companyId,
          auth.user,
          headerCompanyId
        );
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, entityType, companyId]);

  return (
    <div className="relative" ref={rootRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          autoFocus={autoFocus}
          type="text"
          className="input pl-9"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
        />
      </div>
      {/* Floating results — an overlay (absolute) so it never pushes the form
          down; only rendered while focused. */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {loading ? (
            <div className="py-3 text-center text-xs text-muted-foreground">Searching…</div>
          ) : results.length === 0 ? (
            <div className="py-3 text-center text-xs text-muted-foreground">
              {query ? "No matches." : "Start typing to search."}
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect?.({ entityId: r.id, label: r.label });
                      setOpen(false);
                    }}
                    className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">{r.label}</div>
                      {r.sub && (
                        <div className="truncate font-mono text-xs text-muted-foreground">{r.sub}</div>
                      )}
                    </div>
                    <Check className="h-4 w-4 text-brand opacity-0 group-hover:opacity-100" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
